import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { getBaseResourceUrl } from '@service/tenant/tenantResource';
import {
  Button,
  Flex,
  Form,
  Image,
  Row,
  TooltipProps,
  Typography,
  Upload,
  UploadProps,
} from 'antd';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Control, Controller as ControllerHookForm } from 'react-hook-form';

import { env } from 'src/app/env';
import { extractFileNameFromS3Link } from 'src/app/utils/extractFileNameFromS3Link';
import { toFieldStatus } from 'src/app/utils/toFieldStatus';
import { EmptyUploadImage } from 'src/features/Funnel/Components/EmptyUploadImage';
const { Item: FormItem } = Form;
interface FileImage {
  url: string;
  name: string;
}
interface UploadControllerProps extends UploadProps {
  control: Control<any>;
  name: string;
  defaultValue?: string;
  required?: boolean;
  label?: string;
  tooltip?: ReactNode | (TooltipProps & { icon?: ReactElement });
  allowedExtensions?: string[];
  includeResourceBaseUrl?: boolean;
}

export const UploadController: React.FC<UploadControllerProps> = ({
  control,
  name,
  defaultValue,
  required,
  label,
  tooltip,
  allowedExtensions,
  includeResourceBaseUrl,
  ...rest
}) => {
  const [isInvalidFileType, setIsInvalidFileType] = useState(false);
  const [fileImage, setFileImage] = useState<FileImage>({
    url: '',
    name: '',
  } as FileImage);

  const resourceBaseUrl = useMemo(
    () => `${getBaseResourceUrl()}/`,
    [env.ASSETS, env.TENANT_ID]
  );

  const sanitizedAllowedExtensions = useMemo(() => {
    if (!allowedExtensions) return undefined;
    return allowedExtensions.map(ext => ext.replace(/^\./, '').toLowerCase());
  }, [allowedExtensions]);

  const handleChangeFileImageWeb: UploadProps['onChange'] = (
    info: UploadChangeParam<UploadFile>
  ) => {
    getBase64(info.file as RcFile, url => {
      setFileImage({ url, name: info.file.name });
    });
  };

  const verifyFileExtension = (fileName: string) => {
    if (!sanitizedAllowedExtensions) return false;
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return (
      !!fileExtension && sanitizedAllowedExtensions.includes(fileExtension)
    );
  };

  const handleFileValidation = (file: RcFile) => {
    if (!sanitizedAllowedExtensions) return false;
    const isInvalid = !verifyFileExtension(file.name);
    if (isInvalid) {
      setIsInvalidFileType(true);
      return false;
    }
    setIsInvalidFileType(false);
    return false;
  };

  return (
    <ControllerHookForm
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({
        field: { onChange: fieldOnChange, value, ...field },
        fieldState,
      }) => {
        useEffect(() => {
          if (!value) {
            setFileImage({ url: '', name: '' });
          }
        }, [value]);

        const { status } = toFieldStatus(fieldState.error);

        const borderStyleByStatus = useMemo(() => {
          if (status === 'error') return { borderColor: 'red' };
          if (isInvalidFileType) return { borderColor: '#D89614' };
          return {};
        }, [status, isInvalidFileType]);
        return (
          <FormItem
            validateStatus={status}
            label={label}
            {...(tooltip && { tooltip: tooltip })}
            {...(required && { required: required })}
          >
            <Upload.Dragger
              {...rest}
              onChange={info => {
                if (
                  sanitizedAllowedExtensions &&
                  !verifyFileExtension(info.file.name)
                ) {
                  setFileImage({ url: '', name: '' });
                  fieldOnChange('');
                  return;
                }
                handleChangeFileImageWeb(info);
                fieldOnChange(info);
              }}
              style={{ ...borderStyleByStatus }}
              {...field}
              aria-label={label}
              beforeUpload={handleFileValidation}
              accept={sanitizedAllowedExtensions
                ?.map(ext => `.${ext}`)
                .join(',')}
            >
              {fileImage.url || value ? (
                <Row
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingInline: '8px',
                  }}
                >
                  <Flex align='center' gap={8}>
                    <div onClick={e => e.stopPropagation()}>
                      <Image
                        width={60}
                        height={60}
                        preview={{
                          mask: <EyeOutlined />,
                        }}
                        src={
                          typeof value === 'string'
                            ? `${includeResourceBaseUrl ? resourceBaseUrl : ''}${value}`
                            : fileImage.url
                        }
                      />
                    </div>
                    <Typography.Text
                      style={{ marginInlineStart: '16px', color: '#ACACAC' }}
                    >
                      {typeof value === 'string'
                        ? extractFileNameFromS3Link(value)
                        : fileImage.name}
                    </Typography.Text>
                  </Flex>
                  <Button
                    icon={<DeleteOutlined />}
                    type='text'
                    style={{ color: '#ACACAC' }}
                    onClick={e => {
                      e.stopPropagation();
                      setFileImage({ url: '', name: '' });
                      fieldOnChange('');
                    }}
                  />
                </Row>
              ) : (
                <EmptyUploadImage
                  invalid={isInvalidFileType}
                  error={status === 'error'}
                />
              )}
            </Upload.Dragger>
          </FormItem>
        );
      }}
    />
  );
};

const getBase64 = (img: RcFile, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};
